import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Plus, PackageCheck, RefreshCw, Users2 } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";

const STATUSES = ["Pending", "In Progress", "Internal Review", "Sent to Client", "Client Approval", "Revision", "Approved", "Completed"];
const FREQ = ["One Time", "Weekly", "Monthly", "Quarterly", "Annual"];

const empty = { name: "", client_id: "", quantity_promised: 1, quantity_completed: 0, frequency: "One Time", due_date: "", status: "Pending", assigned_team_ids: [], notes: "" };

export default function Deliverables() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [reassignItem, setReassignItem] = useState(null);
  const [statusF, setStatusF] = useState("");
  const [assigneeF, setAssigneeF] = useState("");
  const [clientF, setClientF] = useState("");
  const [freqF, setFreqF] = useState("");
  const [sort, setSort] = useState("due_asc");
  const { user } = useAuth();
  const canManage = user?.role === "super_admin" || user?.role === "admin";
  const range = params.get("range") || "";

  const load = async () => { const r = await api.get("/deliverables"); setItems(r.data); };
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); api.get("/users").then((r) => setUsers(r.data)); }, []);

  const filteredItems = useMemo(() => {
    let data = [...items];
    if (statusF) data = data.filter((d) => d.status === statusF);
    if (assigneeF) data = data.filter((d) => (d.assigned_team_ids || []).includes(assigneeF));
    if (clientF) data = data.filter((d) => d.client_id === clientF);
    if (freqF) data = data.filter((d) => d.frequency === freqF);
    if (range === "upcoming") {
      const today = new Date().toISOString().slice(0, 10);
      const weekEnd = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
      data = data.filter((d) => d.due_date && d.due_date >= today && d.due_date <= weekEnd && !["Completed", "Approved"].includes(d.status));
    }
    data.sort((a, b) => {
      if (sort === "due_asc") return (a.due_date || "9999").localeCompare(b.due_date || "9999");
      if (sort === "due_desc") return (b.due_date || "").localeCompare(a.due_date || "");
      if (sort === "progress_asc") {
        const ap = a.quantity_promised ? a.quantity_completed / a.quantity_promised : 0;
        const bp = b.quantity_promised ? b.quantity_completed / b.quantity_promised : 0;
        return ap - bp;
      }
      if (sort === "progress_desc") {
        const ap = a.quantity_promised ? a.quantity_completed / a.quantity_promised : 0;
        const bp = b.quantity_promised ? b.quantity_completed / b.quantity_promised : 0;
        return bp - ap;
      }
      return 0;
    });
    return data;
  }, [items, statusF, assigneeF, clientF, freqF, range, sort]);

  const save = async () => {
    if (!form.name || !form.client_id) { toast.error("Name and client required"); return; }
    try {
      await api.post("/deliverables", form);
      toast.success("Deliverable created"); setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateProgress = async (d, completed) => {
    try { await api.put(`/deliverables/${d.id}`, { ...d, quantity_completed: completed }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (d, status) => {
    try { await api.put(`/deliverables/${d.id}`, { ...d, status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAssignees = async (ids) => {
    if (!reassignItem) return;
    try {
      await api.put(`/deliverables/${reassignItem.id}`, { ...reassignItem, assigned_team_ids: ids });
      toast.success("Assignees updated"); setReassignItem(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleFormAssignee = (id) => {
    const cur = new Set(form.assigned_team_ids || []);
    if (cur.has(id)) cur.delete(id); else cur.add(id);
    setForm({ ...form, assigned_team_ids: [...cur] });
  };

  return (
    <div className="space-y-6" data-testid="deliverables-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Client Commitments</div>
          <h1 className="text-4xl font-black tracking-tight">Deliverables</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-2">
            {filteredItems.length} of {items.length} shown.
            {range === "upcoming" && (
              <span data-testid="deliv-upcoming-chip" className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-mono text-[10px] uppercase tracking-widest bg-primary/15 text-primary">
                Upcoming (7 days)
                <button data-testid="deliv-upcoming-clear" onClick={() => { const p = new URLSearchParams(params); p.delete("range"); setParams(p); }} className="ml-1 hover:text-foreground">×</button>
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="deliv-refresh-btn" variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw size={14} /></Button>
          {canManage && (
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button data-testid="new-deliverable-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New Deliverable</Button></SheetTrigger>
              <SheetContent className="sm:max-w-md overflow-y-auto">
                <SheetHeader><SheetTitle>New Deliverable</SheetTitle></SheetHeader>
                <div className="mt-5 space-y-3">
                  <div><Label className="font-mono text-[10px] uppercase">Name *</Label><Input data-testid="deliv-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label className="font-mono text-[10px] uppercase">Client *</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                      <SelectTrigger data-testid="deliv-form-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Promised</Label><Input type="number" value={form.quantity_promised} onChange={(e) => setForm({ ...form, quantity_promised: parseInt(e.target.value || 0) })} /></div>
                    <div><Label className="font-mono text-[10px] uppercase">Completed</Label><Input type="number" value={form.quantity_completed} onChange={(e) => setForm({ ...form, quantity_completed: parseInt(e.target.value || 0) })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="font-mono text-[10px] uppercase">Frequency</Label>
                      <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQ.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label className="font-mono text-[10px] uppercase">Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-mono text-[10px] uppercase mb-1">Assignees</Label>
                    <div className="border border-border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                      {users.filter((u) => u.status === "active").map((u) => (
                        <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 px-1.5 py-1 rounded">
                          <input type="checkbox" checked={(form.assigned_team_ids || []).includes(u.id)} onChange={() => toggleFormAssignee(u.id)} />
                          <UserAvatar name={u.name} size={20} /> {u.name}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <Button data-testid="deliv-form-submit" onClick={save} className="w-full bg-primary hover:bg-primary/90">Create Deliverable</Button>
                </div>
              </SheetContent>
            </Sheet>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <Select value={statusF || "__all__"} onValueChange={(v) => setStatusF(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="deliv-status-filter" className="w-44"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={assigneeF || "__all__"} onValueChange={(v) => setAssigneeF(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="deliv-assignee-filter" className="w-48"><SelectValue placeholder="Any assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any assignee</SelectItem>
            {users.filter((u) => u.status === "active").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clientF || "__all__"} onValueChange={(v) => setClientF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Any client" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any client</SelectItem>
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={freqF || "__all__"} onValueChange={(v) => setFreqF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Any frequency" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">Any frequency</SelectItem>{FREQ.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="deliv-sort" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="due_asc">Due date ↑</SelectItem>
            <SelectItem value="due_desc">Due date ↓</SelectItem>
            <SelectItem value="progress_asc">Progress ↑</SelectItem>
            <SelectItem value="progress_desc">Progress ↓</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredItems.length === 0 ? <EmptyState icon={PackageCheck} title={range === "upcoming" ? "Nothing due this week" : "No deliverables match"} /> :
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredItems.map((d) => {
            const client = clients.find((c) => c.id === d.client_id);
            const pct = d.quantity_promised ? Math.round((d.quantity_completed / d.quantity_promised) * 100) : 0;
            const assignees = (d.assigned_team_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
            return (
              <div key={d.id} className="border border-border rounded-md p-5 bg-card" data-testid={`deliverable-card-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground font-mono">{client?.company_name || "—"}</div>
                    <div className="font-display font-extrabold text-lg leading-tight mt-0.5">{d.name}</div>
                  </div>
                  <StatusBadge value={d.status} />
                </div>
                <div className="text-xs text-muted-foreground mt-2 font-mono">{d.frequency} · Due {fmtDate(d.due_date)}</div>

                <div className="mt-3 flex items-center gap-1.5 flex-wrap min-h-6">
                  {assignees.length === 0 ? (
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Unassigned</span>
                  ) : (
                    <>
                      <div className="flex -space-x-1.5">
                        {assignees.slice(0, 4).map((u) => <UserAvatar key={u.id} name={u.name} size={22} className="ring-2 ring-card" />)}
                      </div>
                      <span className="text-[11px] text-muted-foreground truncate">
                        {assignees.slice(0, 2).map((u) => u.name.split(" ")[0]).join(", ")}
                        {assignees.length > 2 && ` +${assignees.length - 2}`}
                      </span>
                    </>
                  )}
                </div>

                <div className="mt-3 font-mono text-sm">
                  <span className="font-bold text-lg">{d.quantity_completed}</span>
                  <span className="text-muted-foreground"> / {d.quantity_promised} · {pct}%</span>
                </div>
                <Progress value={pct} className="mt-2" />
                {canManage && (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Button data-testid={`deliv-inc-${d.id}`} variant="outline" size="sm" onClick={() => updateProgress(d, Math.min(d.quantity_promised, d.quantity_completed + 1))}>+1 done</Button>
                    <Button data-testid={`deliv-reassign-${d.id}`} variant="outline" size="sm" onClick={() => setReassignItem(d)}><Users2 size={12} className="mr-1" />Reassign</Button>
                    <Select value={d.status} onValueChange={(v) => changeStatus(d, v)}>
                      <SelectTrigger className="h-8 text-xs flex-1 min-w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      }

      {/* Reassign sheet */}
      <Sheet open={!!reassignItem} onOpenChange={(v) => !v && setReassignItem(null)}>
        <SheetContent className="sm:max-w-md">
          <SheetHeader><SheetTitle>Assign team — {reassignItem?.name}</SheetTitle></SheetHeader>
          {reassignItem && (
            <ReassignPicker
              users={users.filter((u) => u.status === "active")}
              current={reassignItem.assigned_team_ids || []}
              onSave={saveAssignees}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ReassignPicker({ users, current, onSave }) {
  const [ids, setIds] = useState(new Set(current));
  const toggle = (id) => { const s = new Set(ids); if (s.has(id)) s.delete(id); else s.add(id); setIds(s); };
  return (
    <div className="mt-5 space-y-3">
      <div className="border border-border rounded-md p-2 max-h-72 overflow-y-auto">
        {users.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer" data-testid={`reassign-user-${u.id}`}>
            <input type="checkbox" checked={ids.has(u.id)} onChange={() => toggle(u.id)} />
            <UserAvatar name={u.name} size={22} />
            <div className="flex-1">
              <div className="text-sm">{u.name}</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">{u.designation || u.role}</div>
            </div>
          </label>
        ))}
      </div>
      <Button data-testid="reassign-save" onClick={() => onSave([...ids])} className="w-full bg-primary hover:bg-primary/90">Save assignees</Button>
    </div>
  );
}
